/**
 * Utility functions to validate if image/video URLs are accessible and can be loaded
 */

/**
 * Validate if an image URL is accessible and can be loaded
 * @param url - Image URL to validate
 * @returns Promise<boolean> - true if image can be loaded, false otherwise
 */
export const validateImageUrl = (url: string): Promise<boolean> => {
  return new Promise(resolve => {
    if (!url || !url.trim()) {
      resolve(false)
      return
    }

    const img = new Image()
    const timeout = setTimeout(() => {
      resolve(false)
    }, 10000) // 10 second timeout

    img.onload = () => {
      clearTimeout(timeout)
      resolve(true)
    }

    img.onerror = () => {
      clearTimeout(timeout)
      resolve(false)
    }

    // Handle CORS issues
    img.crossOrigin = 'anonymous'
    img.src = url
  })
}

/**
 * Validate if a direct video URL (MP4) is accessible
 * @param url - Video URL to validate
 * @returns Promise<boolean> - true if video can be loaded, false otherwise
 */
export const validateDirectVideoUrl = (url: string): Promise<boolean> => {
  return new Promise(resolve => {
    if (!url || !url.trim()) {
      resolve(false)
      return
    }

    const video = document.createElement('video')
    const timeout = setTimeout(() => {
      resolve(false)
    }, 10000) // 10 second timeout

    video.onloadedmetadata = () => {
      clearTimeout(timeout)
      resolve(true)
    }

    video.onerror = () => {
      clearTimeout(timeout)
      resolve(false)
    }

    video.src = url
    video.load()
  })
}

/**
 * Validate if a YouTube URL is accessible by checking oEmbed API
 * @param url - YouTube URL to validate
 * @returns Promise<boolean> - true if video exists, false otherwise
 */
export const validateYouTubeUrl = async (url: string): Promise<boolean> => {
  try {
    // Extract video ID from URL
    const videoIdMatch = url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/i,
    )
    if (!videoIdMatch || !videoIdMatch[1]) {
      return false
    }

    const videoId = videoIdMatch[1]
    const oEmbedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`

    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000)
      const response = await fetch(oEmbedUrl, {
        method: 'GET',
        signal: controller.signal,
      })
      clearTimeout(timeoutId)

      if (!response || !response.ok) {
        // Non-2xx responses or missing response indicate an invalid or inaccessible video
        return false
      }

      // Parse JSON to verify the video exists
      const data = await response.json()
      return !!data && !!data.html // oEmbed response should have html field if video exists
    } catch {
      // Network error, timeout, or abort – treat as invalid
      return false
    }
  } catch {
    // If validation fails at any point, treat URL as invalid
    return false
  }
}

/**
 * Validate if a Vimeo URL is accessible by checking oEmbed API
 * @param url - Vimeo URL to validate
 * @returns Promise<boolean> - true if video exists, false otherwise
 */
export const validateVimeoUrl = async (url: string): Promise<boolean> => {
  try {
    // Extract video ID from URL to validate format
    const videoIdMatch = url.match(
      /vimeo\.com\/(?:channels\/[^\/]+\/|groups\/[^\/]+\/videos\/|)(\d+)/i,
    )
    if (!videoIdMatch || !videoIdMatch[1]) {
      return false
    }

    // Use full URL for oEmbed API (Vimeo oEmbed accepts full URL)
    const oEmbedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`

    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000)
      const response = await fetch(oEmbedUrl, {
        signal: controller.signal,
      })
      clearTimeout(timeoutId)

      if (!response || !response.ok) {
        // Non-2xx responses indicate an invalid or inaccessible video
        return false
      }

      const data = await response.json()
      return !!data && !!data.video_id
    } catch {
      // Network error, timeout, or abort – treat as invalid
      return false
    }
  } catch {
    // If validation fails at any point, treat URL as invalid
    return false
  }
}

/**
 * Comprehensive video URL validation
 * Validates format first, then checks if URL is accessible
 * @param url - Video URL to validate
 * @returns Promise<{ isValid: boolean; error?: string }>
 */
export const validateVideoUrlComprehensive = async (
  url: string,
  t: (key: string) => string,
): Promise<{ isValid: boolean; error?: string }> => {
  if (!url || !url.trim()) {
    return { isValid: false, error: t('Please enter a video URL') }
  }

  const trimmedUrl = url.trim()

  // First validate format
  const youtubePattern =
    /(http(s)?:\/\/)?((w){3}.)?youtu(be|.be)?(\.com)?\/(watch\?v=|embed)(\S+)/i
  const vimeoPattern =
    /(http|https)?:\/\/(www\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|)(\d+)(?:\/\?)?/i
  const mp4Pattern = /(http(s)?:)([/.\w\s-])*\.mp4/i

  const isYouTube = youtubePattern.test(trimmedUrl)
  const isVimeo = vimeoPattern.test(trimmedUrl)
  const isDirectMP4 = mp4Pattern.test(trimmedUrl)

  if (!isYouTube && !isVimeo && !isDirectMP4) {
    return {
      isValid: false,
      error: t(
        'Please enter a valid YouTube, Vimeo, or direct video URL (MP4)',
      ),
    }
  }

  // Then validate accessibility (optional, can be disabled if too slow)
  try {
    let isAccessible = false

    if (isYouTube) {
      isAccessible = await validateYouTubeUrl(trimmedUrl)
    } else if (isVimeo) {
      isAccessible = await validateVimeoUrl(trimmedUrl)
    } else if (isDirectMP4) {
      isAccessible = await validateDirectVideoUrl(trimmedUrl)
    }

    // For YouTube/Vimeo, if format is valid, we assume it's accessible
    // (oEmbed might fail due to CORS, but format validation is usually enough)
    if (isYouTube || isVimeo) {
      return { isValid: true }
    }

    // For direct MP4, we check if it's actually accessible
    if (!isAccessible) {
      return {
        isValid: false,
        error: t(
          'The video URL cannot be accessed. Please check if the URL is correct.',
        ),
      }
    }

    return { isValid: true }
  } catch (error) {
    // If validation fails, still allow if format is valid
    // (network issues shouldn't block user)
    return { isValid: true }
  }
}

/**
 * Comprehensive image URL validation
 * Validates format first, then checks if image can be loaded
 * @param url - Image URL to validate
 * @returns Promise<{ isValid: boolean; error?: string }>
 */
export const validateImageUrlComprehensive = async (
  url: string,
  t: (key: string) => string,
): Promise<{ isValid: boolean; error?: string }> => {
  if (!url || !url.trim()) {
    return { isValid: false, error: t('Please enter an image URL') }
  }

  const trimmedUrl = url.trim()

  // Validate format
  const imagePattern = /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i
  const httpPattern = /^https?:\/\//i

  if (!httpPattern.test(trimmedUrl)) {
    return {
      isValid: false,
      error: t('Image URL must start with http:// or https://'),
    }
  }

  if (!imagePattern.test(trimmedUrl)) {
    return {
      isValid: false,
      error: t('Please enter a valid image URL (JPG, PNG, GIF, WEBP, SVG)'),
    }
  }

  // Check if image can be loaded
  try {
    const canLoad = await validateImageUrl(trimmedUrl)
    if (!canLoad) {
      return {
        isValid: false,
        error: t(
          'The image URL cannot be accessed. Please check if the URL is correct.',
        ),
      }
    }
    return { isValid: true }
  } catch (error) {
    // If validation fails, still allow if format is valid
    return { isValid: true }
  }
}
